import type { ContextSchema, StagedContext } from '@/vantage/types/Context.types';
import type { ContextCharacteristic, ExtensionSchema } from '@/vantage/types/Extension.types';
import type { SimplifiedNode } from '@/vantage/types/SimplifiedNode.types';
import type { InstanceConfig } from '@/vantage/types/TableConfig.types';
import type { SeeqUser } from '@/vantage/utilities';
import { serializeNote } from '@/vantage/utilities';
import _ from 'lodash';
import type { Moment } from 'moment';
import {
  combineSourceContextGraphql,
  combineSourceContextMt,
  ContextOperation,
  contextWithMetadata,
  createContext,
  deleteContext,
  getContextIdPair,
  getContextOperation,
  itemIdFromContext,
  nonEvidenceContextMetadataFilters,
  resolveContext,
  suppressionOpaqueLabel,
  suppressionOpaqueNote,
} from '@/vantage/context';
import contextHooks from '@/vantage/contextHooks';
import * as deps from '@/vantage/dependencies';
import * as graphql from '@/vantage/graphql';
import { useStateWithRef } from '@/vantage/hooks';
import * as setup from '@/vantage/setup';
import { t } from 'i18next';
import { SeeqNames } from '@/main/app.constants.seeqnames';
import { LONG_AGO, VantageNames } from '@/vantage/constants';
import { sqItemsApi } from '@/sdk/api/ItemsApi';
import { sqFormulasApi } from '@/sdk/api/FormulasApi';
import { sqContextApi } from '@/sdk/api/ContextApi';
import { sqConditionMonitorsApi } from '@/sdk/api/ConditionMonitorsApi';
import type { ContextCommentInputV1 } from '@/sdk/model/ContextCommentInputV1';
import { sqTableDefinitionsApi } from '@/sdk/api/TableDefinitionsApi';
import { sqWorkbooksApi } from '@/sdk/api/WorkbooksApi';
import type { ItemOutputV1 } from '@/sdk/model/ItemOutputV1';
import type { AnyProperty } from '@/utilities.types';
import { findCategory, findLabels } from '@/vantage/labels';
import { useCallback } from 'react';
import {
  isVantageNameBasedAssetContextEnabled,
  isVantageServerSideRowModelEnabled,
} from '@/services/systemConfiguration.utilities';
import type { AssetBehavior } from '@/vantage/assetBehaviors';
import { reasonCodeLabelIds } from '@/vantage/components/extensionviews/reasonCodes.utilities';
import {
  andFilters,
  equal,
  isNotNull,
  isNull,
  orFilters,
  valueFilter,
} from '@/serverSideTable/materializedTables/filters';
import type { TableDefinitionOutputV1 } from '@/sdk/model/TableDefinitionOutputV1';
import { NUMBER_CONVERSIONS } from '@/main/app.constants';
import { GraphQL } from '@/serverSideTable/materializedTables/materializedTables.constants';

export interface Sample {
  key: number;
  value: number;
  is_uncertain?: boolean | null;
}

export interface Signal {
  samples: { samples: Sample[] };
  metadata: AnyProperty;
}

export async function initializeVantageWorkbook(
  workbookWithProperties: ItemOutputV1,
): Promise<{ evidenceTableDefinition: TableDefinitionOutputV1; workbookConfig: InstanceConfig }> {
  const { data: workbook } = await sqWorkbooksApi.getWorkbook({ id: workbookWithProperties.id });
  const baseProperties = extractWorkbookProperties(workbookWithProperties);
  const propertiesWithoutId = _.isEmpty(baseProperties.extensions)
    ? await setup.initializeVantage(workbookWithProperties.id)
    : baseProperties;

  const properties = { ...propertiesWithoutId, evidenceTableDefinitionId: workbook.evidenceTableDefinitionId! };

  const workbookConfig = await getWorkbookConfig(workbookWithProperties.id, properties);
  const { data: evidenceTableDefinition } = await sqTableDefinitionsApi.getTableDefinition({
    id: workbook.evidenceTableDefinitionId!,
  });
  return { evidenceTableDefinition, workbookConfig };
}

function maybeJson<T>(value: string | undefined, defaultValue: T): T {
  return value === undefined ? defaultValue : JSON.parse(value);
}

function extractWorkbookProperties(workbookWithProperties: ItemOutputV1): {
  extensions: ExtensionSchema[];
  includeExtinct: boolean;
  tableIds?: AnyProperty<string>;
  nameBasedAssetContextOverride: boolean;
  isManualEventsEnabled: boolean;
} {
  const properties = Object.fromEntries(workbookWithProperties.properties.map(({ name, value }) => [name, value]));
  const tableIds: AnyProperty<string> | undefined = properties.tableIds
    ? maybeJson(properties.tableIds, {})
    : undefined;
  return {
    extensions: maybeJson(properties.extensions, []),
    includeExtinct: _.isNil(properties.includeExtinct) || properties.includeExtinct === 'true',
    tableIds,
    nameBasedAssetContextOverride:
      properties.nameBasedAssetContextOverride !== undefined
        ? properties.nameBasedAssetContextOverride === 'true'
        : isVantageNameBasedAssetContextEnabled(),
    isManualEventsEnabled: maybeJson(properties.isManualEventsEnabled, false),
  };
}

export async function getWorkbookConfig(workbookId: string, properties: AnyProperty<any>): Promise<InstanceConfig> {
  const { install, enabled } = setup.getExtensionConfigs(properties.extensions);

  if (install.length > 0) {
    await setup.installExtensions(workbookId, install);
  }

  return {
    workbookId,
    extensions: enabled,
    includeExtinct: properties.includeExtinct,
    tableConfig: setup.tableConfig(),
    tableIds: { evidence: properties.evidenceTableDefinitionId, ...properties?.tableIds },
    useAssetNameForContextLookup: properties.nameBasedAssetContextOverride,
    isManualEventsEnabled: properties.isManualEventsEnabled,
  };
}

export function useLastFetched(key: string) {
  const [timestamps, setTimestamps] = useStateWithRef<Record<string, Date>>({});
  const getLastFetched = useCallback(() => timestamps.current[key] || LONG_AGO, [timestamps, key]);
  const setLastFetched = useCallback(
    (date: Date) => setTimestamps({ ...timestamps.current, [key]: date }),
    [key, timestamps],
  );
  const resetLastFetched = useCallback(
    () => setTimestamps({ ...timestamps.current, [key]: LONG_AGO }),
    [key, timestamps],
  );
  return { getLastFetched, setLastFetched, resetLastFetched };
}

interface ContextWrapper extends ContextSchema {
  itemId?: string;
  context: ContextSchema;
  endTime?: string;
  extensionContexts?: any[];
}

function matchesCompositeId(compositeIds: [string, string][], context: ContextWrapper) {
  return compositeIds.some((compositeId) =>
    _.isEqual(compositeId, [context.itemId?.toLowerCase(), context.kindId.toLowerCase()]),
  );
}

function isRelated(capsuleIds: string[], context: ContextWrapper) {
  const related = context.context?.related?.map((item) => item.capsuleId);
  return related ? capsuleIds.some((capsuleId) => related.includes(capsuleId)) : false;
}

function isActive(context: ContextWrapper, extensions: ExtensionSchema[], conditionMonitorId: string | undefined) {
  const identifier = context.identifier;
  if (identifier === VantageNames.Extensions.Notes) {
    // For suppression opaque, only return notes of the suppressions belonging in this Condition Monitor
    return context.extensionContexts &&
      context.extensionContexts.map((ctx) => ctx.identifier).includes(VantageNames.Extensions.Suppression)
      ? context.context.conditionMonitorId === conditionMonitorId
      : true;
  }

  const extension = extensions.filter((ext) => ext.name === identifier)[0];
  if (!extension) {
    return false;
  }

  const characteristic = extension.contextCharacteristics?.find(
    (c) => c.kind.toLowerCase() === context.kind.toLowerCase(),
  );
  if (characteristic?.eventTimeRepresentation === 'investigationTime') {
    // Event time representation of investigationTime on a capsule context is not supported
    if (context.kind === VantageNames.Kinds.Capsule) {
      return false;
    }
    const now = new Date();
    return (!context.eventStart || context.eventStart <= now) && (!context.eventEnd || context.eventEnd > now);
  }

  return true;
}

const conditionNameCache = new Map<string, string>();
const getConditionName = async (conditionId: string) => {
  if (conditionNameCache.has(conditionId)) {
    return conditionNameCache.get(conditionId)!;
  }
  const conditionName = (await sqItemsApi.getProperty({ id: conditionId, propertyName: 'name' })).data.value;
  conditionNameCache.set(conditionId, conditionName!);
  return conditionName;
};

async function getSuppressionSystemLabelIds() {
  const suppressionLabelCategory = await findCategory(
    `${SeeqNames.SeeqContextNameSpace.UsageRestrictedLabelNamespace}suppression`,
  );
  const suppressionSystemLabels = suppressionLabelCategory ? await findLabels(suppressionLabelCategory.id!) : [];
  return suppressionSystemLabels!.map((label) => label.id!);
}

async function createSuppressionOpaque(labels: AnyProperty[], roomConditionMonitorId: string) {
  const suppressionSystemLabelIds = await getSuppressionSystemLabelIds();
  const suppressionLabels = labels.filter(
    (context) =>
      suppressionSystemLabelIds.includes(context.context) &&
      context.datumId?.toLowerCase() === roomConditionMonitorId?.toLowerCase(),
  );

  const { data: suppressedConditions } = await sqConditionMonitorsApi.getConditionMonitorSuppressions({
    id: roomConditionMonitorId,
  });
  return Promise.all(
    suppressionLabels.map(async (context) => {
      const creatorName = context.createdBy?.fullName ?? 'Unable to retrieve creator name';
      const conditionName = await getConditionName(context.itemId);
      if (context.label === VantageNames.SuppressionLabel.ONGOING) {
        const suppressionExpiration = suppressedConditions.find(
          (suppression) => suppression.conditionId === context.itemId,
        )?.suppressionEndTime;
        return suppressionOpaqueLabel(context, creatorName, conditionName!, suppressionExpiration!);
      } else if (context.label === VantageNames.SuppressionLabel.ENDED_BY_USER) return null;
      else return suppressionOpaqueNote(context, creatorName, conditionName!);
    }),
  );
}
function markAsCapsuleContext(rows: AnyProperty[]): AnyProperty[] {
  return rows.map((r) => {
    return { ...r, isCapsuleContext: true };
  });
}

export async function fetchActiveContext(
  selectedNodes: SimplifiedNode[],
  extensions: ExtensionSchema[] | undefined,
  conditionMonitorId: string | undefined,
  assetBehavior: AssetBehavior,
  evidenceTableId: string | undefined,
  cancellationGroup?: string,
  reasonCodeTableId?: string,
): Promise<ContextSchema[]> {
  // Get Leaf Capsules and their IDs
  const leafCapsules = selectedNodes.flatMap((node) => node.leafCapsules);
  const capsuleIds = selectedNodes
    .flatMap((node) => node.leafCapsules)
    .map((node) => node[VantageNames.Columns.CapsuleID]);
  const compositeCapsuleIds: [string, string][] = leafCapsules.map((capsule) => [
    capsule[VantageNames.Columns.ConditionID].toLowerCase(),
    capsule[VantageNames.Columns.DatumID].toLowerCase(),
  ]);

  // Fetch reason codes (if applicable)
  const conditionIds = isVantageServerSideRowModelEnabled()
    ? selectedNodes.map((node) => node.creationPurview.Condition)
    : [...new Set(leafCapsules.map((c) => c[VantageNames.Columns.ConditionID].toLowerCase()))];

  const reasonCodeIdsPromise = reasonCodeTableId
    ? reasonCodeLabelIds(reasonCodeTableId, conditionIds)
    : Promise.resolve([]);

  // Fetch assets IDs
  const assetIdsPromise = assetBehavior.selectedAssetIds(selectedNodes);

  // Get View Purviews Pairs
  const viewPurviewIdPairs = selectedNodes.flatMap((node) =>
    _.chain(node.viewPurviews)
      .flatMap((viewPurview) => Object.entries(viewPurview))
      .flatMap(([kind, id]) =>
        id &&
        (kind === VantageNames.Kinds.Condition || (kind === VantageNames.Kinds.Asset && assetBehavior.isIdBased()))
          ? [[id]]
          : [],
      )
      .uniqWith(_.isEqual)
      .value(),
  );
  const assetIds = await assetIdsPromise;
  const idPairs = _.chain(viewPurviewIdPairs)
    .concat(assetIds?.map((id) => [id]))
    .uniqWith(_.isEqual)
    .value();

  const datumIdsToInclude = new Set([
    ...leafCapsules.map((row) => row.datumId ?? row[SeeqNames.MaterializedTables.DatumIdColumn]),
    ...(conditionMonitorId ? [conditionMonitorId.toLowerCase()] : []), // for suppression context
    '⛔', // for condition context as datumId is non-nullable and stored as '⛔', and filter operates on db raw values
  ]);
  const { opaque, comments, labels, numerics } = await graphql.allContextForIdPairs(
    idPairs as [string, string][],
    [...datumIdsToInclude],
    cancellationGroup,
  );

  const activeComments = comments.filter((comment) => !comment.archived);

  const suppressionOpaque = conditionMonitorId ? await createSuppressionOpaque(labels, conditionMonitorId) : [];

  const reasonCodeIds = await reasonCodeIdsPromise;

  const filteredContext = contextWithMetadata(
    opaque.concat(suppressionOpaque.filter((ctx) => ctx !== null) as any),
    combineSourceContextGraphql(activeComments, labels, numerics),
    reasonCodeIds,
  ).filter((details) => {
    return (
      ((details.kind === VantageNames.Kinds.Capsule &&
        matchesCompositeId(compositeCapsuleIds as [string, string][], details)) || //capsule context matching capsule composite ID
        isRelated(capsuleIds, details) || // and notes which are related through capsuleIds
        (details.kind !== VantageNames.Kinds.Capsule &&
          (details.identifier === VantageNames.Extensions.Notes || // non-capsule notes
            selectedNodes[0].kind === details.kind))) && // and non-notes matching selected kind
      isActive(details, extensions ?? [], conditionMonitorId)
    );
  });

  return _.sortBy(filteredContext, (context) => context.timeCreated);
}

export async function fetchNonEvidenceContext(
  getLastFetched: () => Date,
  extensions: ExtensionSchema[] | undefined,
  conditionMonitorId: string | undefined,
  nonEvidenceViewPurviews: AnyProperty,
  cancellationGroup?: string,
): Promise<ContextSchema[]> {
  const purviewIds = Object.values(nonEvidenceViewPurviews as object).flatMap((ids: string[]) =>
    ids.map((id): [string] => [id]),
  );

  const lastFetched = getLastFetched();

  // Only include archived when doing an incremental fetch
  const includeArchived = lastFetched !== LONG_AGO;

  const now = Date.now() * NUMBER_CONVERSIONS.NANOSECONDS_PER_MILLISECOND;
  const archivedFilter = includeArchived
    ? null
    : andFilters(
        // Start before now and end after now indicates the context is active
        orFilters(
          valueFilter(VantageNames.TableQueryContextColumns.ContextLabelsStartTime, GraphQL.FilterTypes.LessThan, now),
          isNull(VantageNames.TableQueryContextColumns.ContextLabelsEndTime),
        ),
        orFilters(
          valueFilter(VantageNames.TableQueryContextColumns.ContextLabelsEndTime, GraphQL.FilterTypes.GreaterThan, now),
          isNull(VantageNames.TableQueryContextColumns.ContextLabelsEndTime),
        ),
        equal(VantageNames.TableQueryContextColumns.ContextLabelsArchived, false),
      );

  const { labels } = await graphql.labelContextForIdPairs(
    purviewIds,
    {
      labelFilter: andFilters(
        valueFilter(
          VantageNames.TableQueryContextColumns.ContextLabelsUpdatedAt,
          GraphQL.FilterTypes.GreaterThanOrEqual,
          lastFetched.toISOString(),
        ),
        archivedFilter,
      ),
    },
    cancellationGroup,
  );

  const suppressionOpaque = conditionMonitorId ? await createSuppressionOpaque(labels, conditionMonitorId) : [];
  const filters = nonEvidenceContextMetadataFilters(
    extensions?.filter((ext) => !!ext.contextCharacteristics && ext.name !== VantageNames.Extensions.Notes) ?? [],
  );

  const filteredContext = contextWithMetadata(
    suppressionOpaque.filter((ctx) => ctx !== null),
    combineSourceContextGraphql([], labels, []),
  ).filter((context) => filters.some((filter) => filter(context)));
  return _.sortBy(filteredContext, (context) => context.timeUpdated);
}

export async function fetchEvidenceContext(
  getLastFetched: () => Date,
  evidenceTableId: string | undefined,
  start: Moment,
  end: Moment,
  includeExtinct: boolean,
  cancellationGroup?: string,
  reasonCodeTableId?: string,
): Promise<ContextSchema[]> {
  if (!evidenceTableId) {
    return [];
  }

  const lastFetched = getLastFetched();

  // Only include archived when doing an incremental fetch
  const includeArchived = lastFetched !== LONG_AGO;

  const labels = await graphql.evidenceLabels(
    evidenceTableId,
    andFilters(
      graphql.evidenceLabelsFilter(start, end, lastFetched, includeExtinct, includeArchived),
      isNotNull(VantageNames.TableQueryContextColumns.ContextLabelsId),
    ),
    cancellationGroup,
  );

  const numerics = await graphql.evidenceNumerics(
    evidenceTableId,
    andFilters(
      graphql.evidenceNumericsFilter(start, end, lastFetched, includeExtinct, includeArchived),
      isNotNull(VantageNames.TableQueryContextColumns.ContextNumericId),
    ),
    cancellationGroup,
  );

  const comments = await graphql.evidenceComments(
    evidenceTableId,
    andFilters(
      graphql.evidenceCommentsFilter(start, end, lastFetched, includeExtinct, includeArchived),
      isNotNull(VantageNames.TableQueryContextColumns.ContextCommentsId),
    ),
    cancellationGroup,
  );

  const opaque = await graphql.evidenceOpaque(
    evidenceTableId,
    andFilters(
      graphql.evidenceOpaqueFilter(start, end, lastFetched, includeExtinct, includeArchived),
      isNotNull(VantageNames.TableQueryContextColumns.ContextOpaqueId),
    ),
    cancellationGroup,
  );

  const evidenceOpaque = markAsCapsuleContext(opaque);
  const evidenceLabels = markAsCapsuleContext(labels);
  const evidenceNumerics = markAsCapsuleContext(numerics);
  const evidenceComments = markAsCapsuleContext(comments);

  const conditionIds = evidenceLabels.map((label) => label[SeeqNames.MaterializedTables.ItemIdColumn]);

  const reasonCodeIds = reasonCodeTableId ? await reasonCodeLabelIds(reasonCodeTableId, conditionIds) : [];

  return _.sortBy(
    contextWithMetadata(
      evidenceOpaque,
      combineSourceContextMt(evidenceComments, evidenceLabels, evidenceNumerics),
      reasonCodeIds,
    ),
    (context) => context.timeUpdated,
  );
}

interface PrePostHooks {
  pre?: (params: any) => Promise<any>;
  post?: (params: any) => Promise<any>;
}

interface ContextHook {
  creation?: PrePostHooks;
  resolution?: PrePostHooks;
  deletion?: PrePostHooks;
}

async function withContextHooks(
  { pre, post }: PrePostHooks = { pre: undefined, post: undefined },
  params: any = null,
  fn: () => Promise<any> = Promise.resolve,
) {
  if (pre) await pre(params);
  await fn();
  if (post) await post(params);
}

const serializeRelated = (
  stagedContext: StagedContext[],
  node: SimplifiedNode,
  extensions: ExtensionSchema[],
  existingEvidenceContextKeys: string[],
) =>
  stagedContext
    .flatMap((ctx) => {
      const extension = extensions.find((ext) => ext.name === ctx.identifier);
      const isProxy = extension?.contextCharacteristics?.find((item) => item.kind === node.kind)?.proxyKind === 'leaf';
      const isNote = extension?.name === VantageNames.Extensions.Notes;
      const isDeletion = Object.prototype.hasOwnProperty.call(ctx, 'deletionIds');

      return node.leafCapsules
        .filter((leaf) => {
          if (isNote || !isProxy) {
            return false;
          }
          const leafHasContext = existingEvidenceContextKeys.includes(
            `${leaf[VantageNames.Columns.CapsuleID]}:${ctx.identifier}`,
          );
          return isDeletion ? leafHasContext : !leafHasContext;
        })
        .map((item) => `${item[VantageNames.Columns.CapsuleID]}:${ctx.identifier}`);
    })
    .join(',');

export async function submitContext(
  stagedContext: StagedContext[],
  selectedNodes: SimplifiedNode[],
  extensions: ExtensionSchema[],
  tableIds: AnyProperty<string> | undefined,
  user: SeeqUser | undefined,
  conditionMonitorId: string | undefined,
  activeContext: ContextSchema[],
  evidenceContext: ContextSchema[],
  startOverride?: Moment,
  endOverride?: Moment,
  adjustedContextKind?: string,
  workstepUrl?: string,
) {
  const existingEvidenceContextKeys = evidenceContext.map(
    (condition) => `${condition.conditionId}:${condition.kindId}:${condition.identifier}:${condition.content.value}`,
  );

  // This ensures we create a note, but we should find a way to handle this without a note later - AD-XXXX
  if (!stagedContext.find((ctx) => ctx.identifier === VantageNames.Extensions.Notes)) {
    const stagedContextCopy = [...stagedContext];
    stagedContext.push({
      identifier: VantageNames.Extensions.Notes,
      content: { value: ' ' },
      extensionContexts: stagedContextCopy,
    });
  }

  const deletionOperations: Promise<void>[] = [];
  const deletedIds = new Set<string>();

  await Promise.all(
    selectedNodes.map(async (node) => {
      await Promise.all(
        stagedContext.map(async (ctx) => {
          if (!extensions) return;

          const extension = extensions.find((ext) => ext.name === ctx.identifier);
          if (!extension) return;

          const characteristic = extension.contextCharacteristics?.find(
            (characteristic) => characteristic.kind === node.kind,
          );
          if (!characteristic) return;

          const operation = getContextOperation(ctx, characteristic);
          const { resolution, deletion } = (contextHooks as Record<string, ContextHook>)[ctx.identifier] || {};
          const hookParams = { node, context: ctx, extension, tableIds };

          if (operation === ContextOperation.Resolve || operation === ContextOperation.Delete) {
            ctx.deletionIds?.forEach((deletionId) => {
              if (!deletedIds.has(deletionId)) {
                deletedIds.add(deletionId);

                if (operation === ContextOperation.Resolve) {
                  deletionOperations.push(
                    withContextHooks(resolution, hookParams, () => resolveContext(deletionId, activeContext)),
                  );
                } else {
                  deletionOperations.push(
                    withContextHooks(deletion, hookParams, () =>
                      deleteContext(deletionId, activeContext, characteristic.archiveChildren),
                    ),
                  );
                }
              }
            });
          }
        }),
      );
    }),
  );

  await Promise.all(deletionOperations);

  const creationOperations: Promise<void>[] = [];

  await Promise.all(
    selectedNodes.map(async (node) => {
      const serializedRelated = serializeRelated(stagedContext, node, extensions ?? [], existingEvidenceContextKeys);
      const serializedContext = serializeNote(stagedContext);

      await Promise.all(
        stagedContext.map(async (ctx) => {
          if (!extensions) return;

          const extension = extensions.find((ext) => ext.name === ctx.identifier);
          if (!extension) return;

          const characteristic = extension.contextCharacteristics?.find(
            (characteristic) => characteristic.kind === node.kind,
          );
          if (!characteristic) return;

          const operation = getContextOperation(ctx, characteristic);
          const { creation } = (contextHooks as Record<string, ContextHook>)[ctx.identifier] || {};
          const hookParams = { node, context: ctx, extension, tableIds };

          if (operation === ContextOperation.Add) {
            const [itemId, datumId] = await getContextIdPair(node);
            creationOperations.push(
              withContextHooks(creation, hookParams, () =>
                createContext({
                  ctx,
                  characteristic,
                  node,
                  serializedRelated,
                  serializedContext,
                  user,
                  existingEvidenceContextKeys,
                  activeContext: activeContext?.filter((act) => act.identifier === ctx.identifier),
                  itemId,
                  datumId:
                    (startOverride || endOverride) && adjustedContextKind !== VantageNames.Kinds.Capsule
                      ? null
                      : datumId,
                  conditionMonitorId: conditionMonitorId ?? '',
                  startOverride: startOverride?.toISOString(),
                  endOverride: endOverride?.toISOString(),
                  workstepUrl,
                }),
              ),
            );
          }
        }),
      );
    }),
  );

  await Promise.all(creationOperations);
}

export async function togglePinContext({
  contextId,
  activeContext,
}: {
  contextId: string;
  activeContext: ContextSchema[];
}) {
  const context = activeContext?.filter((ctx) => ctx.id === contextId)[0];
  const itemId = itemIdFromContext(context);

  const input: ContextCommentInputV1 = {
    comment: context.content?.value?.toString(),
    datumId: context.datumId,
    startTime: context.eventStart?.toISOString(),
    endTime: context.eventEnd?.toISOString(),
    isPinned: !context.isPinned,
  };

  await sqContextApi.updateContextComment(input, { itemId, commentId: contextId });
}

export function addCharacteristicsForPurviewFields(extension: ExtensionSchema, purviewFields: string[]) {
  const template = extension.contextCharacteristics?.[0];
  const kinds = extension.contextCharacteristics?.map((c) => c.kind) ?? [];
  const newPurviewFields: ContextCharacteristic[] =
    purviewFields
      ?.filter((field) => !kinds.includes(field))
      ?.map((field) => ({ ...template, kind: field, eventTimeRepresentation: undefined })) ?? [];
  return {
    ...extension,
    contextCharacteristics: newPurviewFields
      ? extension.contextCharacteristics?.concat(newPurviewFields)
      : extension.contextCharacteristics,
  };
}

const QUICK_TREND_TYPES = ['CalculatedScalar', 'CalculatedSignal', 'StoredScalar', 'StoredSignal'];

export async function fetchQuickTrend(start: Moment, end: Moment, conditionIds: string[]): Promise<any[]> {
  const laneWidth = 2000;
  const dependencies = _.chain(await Promise.all(conditionIds.map((id) => deps.getDependencies(id))))
    .flatMap((ds) => deps.flatten(ds))
    .filter((d) => QUICK_TREND_TYPES.includes(d.type))
    .uniqBy((d) => d.fingerprint)
    .value();
  if (dependencies.length === 0) {
    throw new Error(t('VANTAGE.ERROR.NO_SIGNALS'));
  } else if (dependencies.length > 10) {
    throw new Error(t('VANTAGE.ERROR.TOO_MANY_SIGNALS'));
  }

  const duration = end.valueOf() - start.valueOf();
  const resolution = duration / laneWidth;
  return await Promise.all(
    dependencies.map(async (dep) => {
      const {
        data: { metadata, samples },
      } = await sqFormulasApi.runFormula({
        start: start.toISOString(),
        end: end.toISOString(),
        formula: ['StoredScalar', 'CalculatedScalar'].includes(dep.type)
          ? `$sig = $param.toSignal() $sig.spikeCatcher(${resolution}ms).parallelize()`
          : `$param.spikeCatcher(${resolution}ms).parallelize()`,
        parameters: [`param=${dep.id}`],
        limit: laneWidth * 5,
      });
      return [{ [dep.id]: deps.assetInfo(dep) }, { metadata, samples }];
    }),
  );
}
